// Context
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../common/Modal";
import { useChats } from "../../../hooks/useChats";
import Autocomplete from "../../common/Autocomplete";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import api from "../../../services/api";
import ModalIC10OptimizerNote from "./ModalIC10OptimizerNote";
import { appendCodeSelections, normalizList } from "../../../utils/utils";
import { buildEvidenceMap, evidenceForLabel, getSpecLevel, getEvidenceLevel } from "../../../utils/icd10Evidence";
import { useRipple } from "../../../hooks/useRipple";

// Grouped-mode contract:
//   showEditContextVariable.groups = [
//     { concept: "Cardiovascular",
//       candidates: ["I21.4 - ...", "I21.9 - ...", "I25.10 - ..."],
//       mode: "single" | "multi" },
//     ...
//   ]
// Candidates are plain string labels. Peer-code "alternatives" at adjacent
// specificity (e.g. M54.59 / M54.50 / M54.51) are derived at render time from
// the candidate list itself — any two candidates in the same group that share
// the same chapter prefix (M54, I21) are peers of each other.
// When `groups` is absent and the variable is billableCodesICD10, we cluster the
// flat `labels` array by ICD-10 body system so heart codes bucket together, COPD
// codes bucket together, etc. Top candidate is preselected.

const SYSTEM_LABELS = {
  CARDIO: "Cardiovascular",
  RESPIRATORY: "Respiratory",
  GU: "Genitourinary",
  MSK: "Musculoskeletal",
  ENDOCRINE: "Endocrine & Metabolic",
  NERVOUS: "Neurologic",
  DIGESTIVE: "Gastrointestinal",
  SYMPTOMS: "Symptoms & Signs",
  INJURY: "Injury",
  NEOPLASMS: "Neoplasms",
  BLOOD: "Blood",
  MENTAL: "Mental Health",
  EYE: "Eye",
  EAR: "Ear",
  SKIN: "Skin",
  PREGNANCY: "Pregnancy",
  PERINATAL: "Perinatal",
  CONGENITAL: "Congenital",
  INFECTIOUS: "Infectious",
  EXTERNAL: "External Causes",
  HISTORY: "History & Status",
  OTHER: "Other",
};

// Map an ICD-10 code (e.g. "I21.4", "H35.00", "D61.9") to its body system.
// Follows ICD-10-CM chapter block ranges.
function extractSystem(label) {
  const code = (label.split(" - ")[0] || "").trim().toUpperCase();
  if (!code) return "OTHER";
  const letter = code[0];
  const twoDigits = parseInt(code.slice(1, 3), 10);

  switch (letter) {
    case "A":
    case "B":
      return "INFECTIOUS";
    case "C":
      return "NEOPLASMS";
    case "D":
      return twoDigits >= 50 ? "BLOOD" : "NEOPLASMS";
    case "E":
      return "ENDOCRINE";
    case "F":
      return "MENTAL";
    case "G":
      return "NERVOUS";
    case "H":
      return twoDigits >= 60 ? "EAR" : "EYE";
    case "I":
      return "CARDIO";
    case "J":
      return "RESPIRATORY";
    case "K":
      return "DIGESTIVE";
    case "L":
      return "SKIN";
    case "M":
      return "MSK";
    case "N":
      return "GU";
    case "O":
      return "PREGNANCY";
    case "P":
      return "PERINATAL";
    case "Q":
      return "CONGENITAL";
    case "R":
      return "SYMPTOMS";
    case "S":
    case "T":
      return "INJURY";
    case "V":
    case "W":
    case "X":
    case "Y":
      return "EXTERNAL";
    case "Z":
      return "HISTORY";
    default:
      return "OTHER";
  }
}

function clusterBySystem(labels) {
  const bySystem = new Map();
  const seenCodes = new Set();
  labels.forEach((label, i) => {
    // Dedupe by leading code — backend sometimes echoes a code the encounter
    // already has, which would otherwise render twice.
    const code = (label.split(" - ")[0] || "").trim().toUpperCase();
    if (!code || seenCodes.has(code)) return;
    seenCodes.add(code);
    const key = extractSystem(label);
    if (!bySystem.has(key)) {
      bySystem.set(key, { key, candidates: [], order: i });
    }
    bySystem.get(key).candidates.push(label);
  });
  // Preserve first-appearance order so the system containing the overall top-ranked
  // candidate renders first. Mode is multi so providers can add more than one code
  // from the same body system (e.g. accept both acute + chronic variants).
  return [...bySystem.values()]
    .sort((a, b) => a.order - b.order)
    .map((g) => ({
      concept: SYSTEM_LABELS[g.key] || g.key,
      systemKey: g.key,
      candidates: g.candidates,
      mode: "multi",
    }));
}

// A candidate is either a plain string label (legacy / default) or an object with
// optional server-supplied `alternatives`. labelOf reads either shape so the rest
// of the modal can treat candidates uniformly.
function labelOf(candidate) {
  return typeof candidate === "string" ? candidate : candidate?.label || "";
}

// Strip trailing ICD-10-CM version annotations like "[V28]" from a description.
// Clinical noise in the picker view; full label is preserved in state so the
// save-path output is unchanged.
const V_TAG_RE = /\s*\[V\d+\]\s*$/i;
function cleanDescription(desc) {
  return (desc || "").replace(V_TAG_RE, "").trim();
}

// Dedupe candidates by their leading ICD code. Preserves first occurrence.
// Works on both string and object candidate shapes.
function dedupeCandidates(candidates) {
  if (!Array.isArray(candidates)) return candidates;
  const seen = new Set();
  return candidates.filter((c) => {
    const code = (labelOf(c).split(" - ")[0] || "").trim().toUpperCase();
    if (!code || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
}

// Real peer-code discovery. Within a group the AI already returned several ranked
// candidates for the same body system. Peers at adjacent specificity are the ones
// sharing the same chapter prefix (e.g. "M54" for M54.50 / M54.51 / M54.59, or
// "I21" for I21.0 / I21.4 / I21.9). We use the existing candidate list as the
// source — no mock data, no extra backend call.
function peerFamilyFor(targetLabel, groupCandidates) {
  const targetCode = (targetLabel.split(" - ")[0] || "").trim();
  if (!targetCode) return [];
  const family = targetCode.split(".")[0];
  return (groupCandidates || [])
    .map(labelOf)
    .filter((l) => l && l !== targetLabel)
    .filter((l) => {
      const code = (l.split(" - ")[0] || "").trim();
      return code && code.split(".")[0] === family;
    });
}

function ModalEditContextVariable({ onSave }) {
  const { t } = useTranslation();
  const createRipple = useRipple();
  const [value, setValue] = useState();
  const [selectedByGroup, setSelectedByGroup] = useState({});
  // Alternatives submenu: single-open. Key is `${groupIndex}:${label}` — only one
  // row may have its peer-code submenu expanded at a time. Opening another row
  // closes the previous (per spec §6).
  const [openAlternativesKey, setOpenAlternativesKey] = useState(null);
  // Codes added via the inline search bar (fallback for when the AI didn't
  // suggest a code the provider needs). Stored as plain labels so they flow
  // through the same clustering + selection path as AI-returned candidates.
  const [manualAdditions, setManualAdditions] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [showOptimizerNote, setShowOptimizerNote] = useState(false);
  // Labels that are the RESULT of a swap. The chevron disappears on these
  // rows so the alternatives submenu can't keep carouseling back around.
  // Once the provider has picked an alternative, the decision is committed;
  // if they want to change again they can toggle the row off and search again.
  const [swappedRows, setSwappedRows] = useState(() => new Set());
  const [options, setOptions] = useState([]);
  const { chat, showEditContextVariable, setShowEditContextVariable, updateContext } = useChats();

  const variable = showEditContextVariable?.variable;
  const serverGroups = showEditContextVariable?.groups;
  const isServerGrouped = Array.isArray(serverGroups) && serverGroups.length > 0;

  const inferredGroups = useMemo(() => {
    if (isServerGrouped) return null;
    // Auto-cluster only for ICD-10. Other variables keep flat multi-select.
    if (variable !== "billableCodesICD10") return null;
    const baseLabels = showEditContextVariable?.labels || [];
    const allLabels = [...baseLabels, ...manualAdditions];
    if (allLabels.length === 0) return null;
    return clusterBySystem(allLabels);
  }, [isServerGrouped, showEditContextVariable, variable, manualAdditions]);

  const effectiveGroups = useMemo(() => {
    const base = isServerGrouped ? serverGroups : inferredGroups;
    if (!base) return base;
    // Dedupe candidates within each server-provided group so the same code
    // can never render twice on screen.
    return base.map((g) => ({ ...g, candidates: dedupeCandidates(g.candidates || []) }));
  }, [isServerGrouped, serverGroups, inferredGroups]);

  // Compliance evidence map — reads the already-scored ICD-10 list on chat
  // context so picker rows can surface the same SPEC% / HPI / PLAN / V28
  // signals the Optimize modal renders. No new backend call. When a voice
  // candidate isn't in the scored list yet the lookup returns null and the
  // row renders without badges (honest silence over random color).
  const scoredIcd10 = chat?.contextVariables?.["local:billableCodesICD10"];
  const evidenceMap = useMemo(() => {
    if (variable !== "billableCodesICD10") return null;
    return buildEvidenceMap(normalizList(scoredIcd10));
  }, [variable, scoredIcd10]);

  const hasGroups = Array.isArray(effectiveGroups) && effectiveGroups.length > 0;
  const isFlatMulti = !hasGroups && showEditContextVariable?.labels?.length > 1;

  const altKeyFor = (groupIndex, label) => groupIndex + ":" + label;
  const isAlternativesOpen = (groupIndex, label) => openAlternativesKey === altKeyFor(groupIndex, label);
  const openAlternatives = (groupIndex, label) => {
    const key = altKeyFor(groupIndex, label);
    setOpenAlternativesKey((prev) => (prev === key ? null : key));
  };

  useEffect(() => {
    if (!showEditContextVariable) {
      setValue(undefined);
      setSelectedByGroup({});
      setOpenAlternativesKey(null);
      setManualAdditions([]);
      setSearchOptions([]);
      setSwappedRows(new Set());
      setOptions([]);
      return;
    }

    if (hasGroups) {
      const initial = {};
      effectiveGroups.forEach((g, gi) => {
        const first = g?.candidates?.[0];
        initial[gi] = first ? [labelOf(first)] : [];
      });
      setSelectedByGroup(initial);
      setOpenAlternativesKey(null);
      setSwappedRows(new Set());
      return;
    }

    if (showEditContextVariable.labels) {
      const deduped = dedupeCandidates(showEditContextVariable.labels);
      setOptions(
        deduped.map((item) => ({
          key: item,
          label: item,
        }))
      );

      if (showEditContextVariable.labels.length === 1) {
        setValue(showEditContextVariable.labels[0]);
        setSelectedByGroup({ 0: [showEditContextVariable.labels[0]] });
      } else {
        // Flat multi-select fallback (non-ICD variable). Preselect top-ranked only.
        setSelectedByGroup({ 0: [showEditContextVariable.labels[0]] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditContextVariable]);

  const selectInGroup = (gi, label, mode) => {
    setSelectedByGroup((prev) => {
      const current = prev[gi] || [];
      if (mode === "multi") {
        const next = current.includes(label) ? current.filter((v) => v !== label) : [...current, label];
        return { ...prev, [gi]: next };
      }
      return { ...prev, [gi]: current.includes(label) ? [] : [label] };
    });
  };

  const toggleFlat = (label) => {
    setSelectedByGroup((prev) => {
      const current = prev[0] || [];
      const next = current.includes(label) ? current.filter((v) => v !== label) : [...current, label];
      return { ...prev, 0: next };
    });
  };

  // Inline ICD-10 lookup — same `api.getIcd10` endpoint the manual-add modal uses.
  // Provider types ≥3 characters, sees matching codes in the Autocomplete dropdown,
  // picks one to drop it into the list as a confirmed row. Zero round-trip to the
  // voice flow; just reuse the existing code search.
  const fetchCodes = async (search) => {
    try {
      const res = await api.getIcd10(search);
      const list = (res?.data?.result || []).slice().sort((a, b) => String(a.n || "").localeCompare(String(b.n || "")));
      setSearchOptions(
        list.map((item) => {
          const value = item.n + " - " + item.d;
          return { key: value, label: value, value: item };
        })
      );
    } catch (e) {
      console.error("ICD-10 search failed", e);
    }
  };
  const fetchCodesDebounce = useDebouncedCallback(fetchCodes, 500);
  const onTypeSearch = (text) => {
    if (text && text.length > 2) fetchCodesDebounce(text);
    else setSearchOptions([]);
  };
  const addManualCode = (result) => {
    if (!result) return;
    const code = String(result.n || "").trim();
    if (!code) return;
    const label = code + " - " + (result.d || "");
    const normCode = code.toUpperCase();

    // Already present in AI-returned candidates or prior manual additions?
    // If yes, don't duplicate — just ensure it's confirmed in its group. Gather
    // all matches first, then one batched setState so React renders once.
    const matches = [];
    (effectiveGroups || []).forEach((g, gi) => {
      (g.candidates || []).forEach((c) => {
        const cc = (labelOf(c).split(" - ")[0] || "").trim().toUpperCase();
        if (cc === normCode) matches.push({ gi, label: labelOf(c) });
      });
    });
    if (matches.length > 0) {
      setSelectedByGroup((prev) => {
        const next = { ...prev };
        for (const m of matches) {
          const current = next[m.gi] || [];
          if (!current.includes(m.label)) next[m.gi] = [...current, m.label];
        }
        return next;
      });
      toast.info(t("modals.icd10AddCode.codeAlreadyPresent"));
      setSearchOptions([]);
      return;
    }

    // New code — push onto manual additions. inferredGroups will re-cluster on
    // next render; predict which group index hosts this system so we can mark
    // the new row as confirmed synchronously.
    const system = extractSystem(label);
    let gi = (effectiveGroups || []).findIndex((g) => g.systemKey === system);
    if (gi === -1) gi = (effectiveGroups || []).length;

    setManualAdditions((prev) => [...prev, label]);
    setSelectedByGroup((prev) => ({ ...prev, [gi]: [...(prev[gi] || []), label] }));
    setSearchOptions([]);
  };

  // Swap a selected row's code for one of its peer-family codes. Preserves position
  // of other selected rows. Submenu collapses after a swap so the list settles on
  // the new choice instead of hanging open.
  const swapInGroup = (gi, oldLabel, newLabel) => {
    if (oldLabel === newLabel) return;
    setSelectedByGroup((prev) => {
      const current = prev[gi] || [];
      const idx = current.indexOf(oldLabel);
      if (idx === -1) {
        // Old wasn't selected (shouldn't happen in practice). Add new without removing.
        if (current.includes(newLabel)) return prev;
        return { ...prev, [gi]: [...current, newLabel] };
      }
      const next = [...current];
      next[idx] = newLabel;
      // Dedupe in case newLabel was already elsewhere in the selection.
      const seen = new Set();
      return { ...prev, [gi]: next.filter((v) => (seen.has(v) ? false : seen.add(v))) };
    });
    setSwappedRows((prev) => {
      const n = new Set(prev);
      n.add(newLabel);
      return n;
    });
    setOpenAlternativesKey(null);
  };

  const collectedValues = useMemo(() => {
    if (hasGroups || isFlatMulti) {
      return Object.values(selectedByGroup).flat();
    }
    return value ? [value] : [];
  }, [hasGroups, isFlatMulti, selectedByGroup, value]);

  const update = () => {
    const valuesToAdd = collectedValues;

    if (valuesToAdd.length === 0) {
      return toast.error(t("modals.editContextVariable.toast.selectValue"));
    }

    if (onSave) {
      setShowEditContextVariable();
      return onSave(hasGroups || isFlatMulti ? valuesToAdd : value);
    }

    // Merge into the encounter's CURRENT list, whatever shape it is stored in
    // (array, dict, or a JSON string -- see normalizList). Dedupes against the
    // codes already present so a voice suggestion that echoes an existing code
    // never produces a duplicate row. This used to start from [] whenever the
    // stored value was a string, which overwrote every existing code with just
    // the new one once JSON-string lists started rendering.
    const newList = appendCodeSelections(
      chat.contextVariables["local:" + showEditContextVariable.variable],
      valuesToAdd,
      showEditContextVariable.variable
    );

    if (showEditContextVariable.callback) {
      showEditContextVariable.callback(newList);
    } else {
      updateContext(showEditContextVariable.variable, newList);
    }
    setShowEditContextVariable();
  };

  // Build a scoped {code, description}[] from the picker's confirmed
  // selections so the note-optimizer modal generates addendums for exactly
  // these codes — not the entire encounter's billableCodesICD10 list.
  const confirmedIcdList = useMemo(() => {
    if (!hasGroups) return null;
    const out = [];
    const seen = new Set();
    for (const val of collectedValues) {
      const dash = val.indexOf(" - ");
      if (dash < 0) continue;
      const code = val.substring(0, dash).trim();
      const normCode = code.toUpperCase();
      if (!code || seen.has(normCode)) continue;
      seen.add(normCode);
      out.push({ code, description: cleanDescription(val.substring(dash + 3)) });
    }
    return out;
  }, [hasGroups, collectedValues]);

  const openOptimizerNote = () => {
    setShowOptimizerNote(true);
  };

  const renderCodeRow = ({ label, checked, onToggle, rowKey, alternatives, altOpen, onAltToggle }) => {
    const parts = label.split(" - ");
    const code = parts[0];
    // Strip trailing version tags like "[V28]" — clinical noise for the picker view;
    // they stay in the underlying label so save-path output is unchanged.
    const description = cleanDescription(parts.slice(1).join(" - ") || label);

    const hasPeers = Array.isArray(alternatives) && alternatives.length > 0;
    const ev = evidenceForLabel(label, evidenceMap);
    // Render the evidence column for every ICD-10 row (even when grading is
    // missing) so SPEC / HPI / PLAN / V28 cells stay on fixed vertical planes
    // across rows. Missing fields render muted; present fields get color.
    const showBadgeColumn = !!evidenceMap;

    return (
      <div
        key={rowKey}
        className={"dx-row" + (checked ? " is-confirmed" : " is-suggested") + (altOpen ? " alts-open" : "")}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span className="dx-row-dot" aria-hidden="true" />
        <span className="dx-row-pill">{code}</span>
        <span className="dx-row-desc">{description}</span>
        {showBadgeColumn && (
          <span className="dx-row-badges" aria-hidden="true">
            <span className={"dx-badge dx-badge-spec " + (ev?.specificity != null ? getSpecLevel(ev.specificity) : "empty")}>
              {ev?.specificity != null ? "SPEC " + ev.specificity + "%" : "SPEC"}
            </span>
            <span className={"dx-badge " + (ev?.hpi != null ? getEvidenceLevel(ev.hpi) : "empty")}>HPI</span>
            <span className={"dx-badge " + (ev?.planOfCare != null ? getEvidenceLevel(ev.planOfCare) : "empty")}>PLAN</span>
            <span className={"dx-badge " + (ev?.v28 ? "green" : "empty")}>V28</span>
          </span>
        )}
        <span className="dx-row-status" aria-hidden="true">
          {checked && <i className="notranslate material-icons dx-row-check">check</i>}
        </span>
        {hasPeers && (
          <button
            type="button"
            className={"dx-row-chev" + (altOpen ? " open" : "")}
            aria-label={t("modals.editContextVariable.alternativesAffordance")}
            aria-expanded={!!altOpen}
            onClick={(e) => {
              e.stopPropagation();
              if (onAltToggle) onAltToggle();
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <i className="notranslate material-icons">expand_more</i>
          </button>
        )}
      </div>
    );
  };

  const renderAlternativesSubmenu = (gi, currentLabel, peers) => {
    const currentParts = currentLabel.split(" - ");
    const currentCode = currentParts[0];
    const currentDesc = cleanDescription(currentParts.slice(1).join(" - ") || currentLabel);

    return (
      <div
        className="dx-alts-submenu"
        role="group"
        aria-label={t("modals.editContextVariable.alternativesHeading", { description: currentDesc })}
      >
        <button type="button" className="dx-alt-row dx-alt-keep" onClick={() => setOpenAlternativesKey(null)}>
          <span className="dx-alt-keep-label">{t("modals.editContextVariable.keepCurrent")}</span>
          <span className="dx-alt-pill current">{currentCode}</span>
        </button>
        {peers.map((peerLabel) => {
          const parts = peerLabel.split(" - ");
          const code = parts[0];
          const description = cleanDescription(parts.slice(1).join(" - ") || peerLabel);
          const peerEv = evidenceForLabel(peerLabel, evidenceMap);
          return (
            <button key={peerLabel} type="button" className="dx-alt-row" onClick={() => swapInGroup(gi, currentLabel, peerLabel)}>
              <span className="dx-alt-pill">{code}</span>
              <span className="dx-alt-desc">{description}</span>
              {peerEv?.specificity != null && (
                <span className={"dx-alt-spec " + getSpecLevel(peerEv.specificity)}>{peerEv.specificity}%</span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  const renderGroups = () => (
    <div className="dx-groups">
      {effectiveGroups.map((g, gi) => {
        const mode = g.mode || "multi";
        const candidates = g.candidates || [];
        const selected = selectedByGroup[gi] || [];
        const visible = candidates.map(labelOf).filter(Boolean);
        if (visible.length === 0) return null;

        return (
          <section key={gi} className="dx-group">
            <div className="dx-group-head">{g.concept || g.systemKey || ""}</div>

            {visible.map((label) => {
              // Once a row is the result of a user swap, suppress its chevron
              // so the picker doesn't carousel through peers indefinitely.
              const peers = swappedRows.has(label) ? [] : peerFamilyFor(label, candidates);
              const altOpen = isAlternativesOpen(gi, label);
              const isSelected = selected.includes(label);
              return (
                <div key={label} className="dx-row-wrap">
                  {renderCodeRow({
                    rowKey: label,
                    label,
                    checked: isSelected,
                    onToggle: () => selectInGroup(gi, label, mode),
                    alternatives: peers,
                    altOpen,
                    onAltToggle: () => openAlternatives(gi, label),
                  })}
                  {altOpen && peers.length > 0 && renderAlternativesSubmenu(gi, label, peers)}
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );

  const renderFlatMultiSelect = () => {
    const selected = selectedByGroup[0] || [];
    return (
      <div className="code-select-list">
        {options.map((option, i) =>
          renderCodeRow({
            rowKey: option.key,
            label: option.label,
            checked: selected.includes(option.key),
            onToggle: () => toggleFlat(option.key),
            top: i === 0,
          })
        )}
      </div>
    );
  };

  const totalSelected = collectedValues.length;
  const updateLabel =
    totalSelected > 0
      ? t("modals.editContextVariable.updateWithCount", { count: totalSelected })
      : t("modals.editContextVariable.update");

  const totalCandidates = hasGroups ? effectiveGroups.reduce((sum, g) => sum + (g.candidates || []).length, 0) : 0;
  const suggestedCount = Math.max(0, totalCandidates - totalSelected);

  const modalTitle = hasGroups ? t("modals.editContextVariable.confirmTitle") : showEditContextVariable?.title;

  return (
    <Modal
      show={showEditContextVariable}
      close={() => setShowEditContextVariable()}
      size="medium"
      hideTitle
      className={"edit-context-variable-modal context-picker dx-modal"}
    >
      <div className="dx-head">
        <h2 className="dx-title">{modalTitle}</h2>
        {hasGroups && (
          <div className="dx-meta">
            <span className="dx-meta-confirmed">{t("modals.editContextVariable.confirmedCount", { count: totalSelected })}</span>
            <span className="dx-meta-sep" aria-hidden="true">
              ·
            </span>
            <span>{t("modals.editContextVariable.suggestedCount", { count: suggestedCount })}</span>
            <span className="dx-meta-sep" aria-hidden="true">
              ·
            </span>
            <span className="dx-meta-hint">{t("modals.editContextVariable.tapToRemove")}</span>
          </div>
        )}
      </div>

      <div className="dx-body">
        {hasGroups && (
          <div className="dx-search">
            <i className="notranslate material-icons dx-search-icon">search</i>
            <Autocomplete
              onChange={onTypeSearch}
              onSelect={(e) => e && addManualCode(e.value)}
              data={searchOptions}
              placeholder={t("modals.icd10AddCode.addCodePlaceholder")}
              resetOnSelect={true}
            />
          </div>
        )}
        {hasGroups ? (
          renderGroups()
        ) : isFlatMulti ? (
          renderFlatMultiSelect()
        ) : (
          <Autocomplete initValue={value} onSelect={(e) => setValue(e?.key)} data={options} />
        )}
      </div>

      <div className="dx-footer">
        <div className="dx-footer-left">
          {hasGroups && totalSelected > 0 && (
            <button type="button" className="dx-link dx-link-accent" onClick={openOptimizerNote}>
              <i className="notranslate material-symbols-outlined">auto_awesome</i>
              <span>{t("modals.editContextVariable.optimizeDocs")}</span>
            </button>
          )}
        </div>
        <button
          type="button"
          className="dx-primary"
          onClick={(e) => {
            createRipple(e);
            update();
          }}
          disabled={totalSelected === 0}
        >
          {updateLabel}
        </button>
      </div>

      {showOptimizerNote && (
        <ModalIC10OptimizerNote
          close={() => setShowOptimizerNote(false)}
          restoreScroll={() => {}}
          initialIcd10={confirmedIcdList}
        />
      )}
    </Modal>
  );
}

export default ModalEditContextVariable;
